// -----------------------------------------------------------------------------
// DO NOT REQUIRE THIS FILE DIRECTLY, USE utils/ajax.js INSTEAD
//
// YOU COULD EASILY GET INTO DEPENDENCY-HELL IF YOU REQUIRE THIS.
// YOU HAVE BEEN WARNED.
// -----------------------------------------------------------------------------

import Promise from 'Promise';
import AuthStore from '@get-bridge/learn-legacy-shared/dist/stores/AuthStore';
import $ from '@get-bridge/learn-legacy-shared/dist/utils/jquery';
import FreeTrialService from '@get-bridge/learn-legacy-shared/dist/services/FreeTrialService';
import RouteActions from '@get-bridge/learn-legacy-shared/dist/core/RouteActions';
import { isPlatformActive, reissueAuthentication } from '@get-bridge/tapestry-sdk';

export const isXHRAborted = (xhr, statusText) => {
  return statusText === 'abort' || (!xhr.getAllResponseHeaders() && xhr.status === 0);
};

function adjustRuntimeURLs(options) {
  if (isPlatformActive()) {
    if (options.url.startsWith('/api') && !options.url.startsWith('/api/r2b')) {
      return {
        ...options,
        url: options.url.replace('/api', '/api/learn')
      };
    } else if (options.url === '/runtime.json' || options.url === '/guest_runtime.json') {
      return {
        ...options,
        url: options.url.replace('/', '/learn/')
      };
    }
  }
  return options;
}

// Hi.
//
// This is the underlying "adapter" that all our ajax calls are made with (under
// the hood, of course, as we use [utils/ajax.js]() to do XHRs.)
//
// We want the logic that goes here applied to _all_ requests so we had to work
// out through some hoops to make it as seamless as possible.
//
// Here's what's happening:
//
// - if the XHR fails with a 401 and the user is currently logged in, their
//   token might have expired so we try to re-issue that token and retry the
//   original XHR
//
// - if the XHR fails with a 403 and the user is currently logged in, we notify
//   them nicely that they are not authorized to do whatever they were trying to
//   do and then redirect them to the appropriate landing page based on their
//   role (author -> /insights, learner -> my learning, etc.)
//
// - if the XHR is aborted, the request will reject if `rejectOnAbort` is true,
//   and do nothing otherwise.
//   it is set to false by default to make sure that the application won't
//   display toast errors for an aborted request. we use rejectOnAbort = true
//   in the tests to make sure that `done()` is called.
//
// - otherwise, if the statuses are not 401/403 or the user isn't logged in, we
//   do nothing special
//
// @param {Object} ajaxOptions
//        The argument for `$.ajax()`.
//
// @return {Object}   service
// @return {Function} service.then
//         A hook into the promise that represents the party that is this.
//         The promise will be fulfilled only after any token-reissuing or
//         whatever crap we had to do was actually done.
//
// @return {jqXHR}    service.jqXHR
//         OK this is probably the lamest return value of 2015. I'll try to
//         explain why.
//
//         So, this will point to the jqXHR we receive when we make the _FIRST_
//         $.ajax call. Since we might end up retrying that original XHR, this
//         handle will become "stale" and point to the jqXHR for the initial
//         request which is now dead.
//
//         The unfortunate effect of this is that for our current utils/ajax.js
//         implementation, the middlewares will run ONLY on the initial call
//         (in other words, the middlewares will not be applied if we *had* to
//         re-perform.)
//
//         We could get fancy and make a callback for every jqXHR we generate
//         and have utils/ajax.js hook into that but right now, I'm not feeling
//         fancy enough.
module.exports = function(ajaxOptions) {
  const { resolve, reject, promise } = defer();
  const {
    toastOnUnauthorized,
    rerouteOnUnauthorized = true,
    rejectOnAbort = false,
    ...remainingAjaxOptions
  } = ajaxOptions;
  const adjustedAjaxOptions = adjustRuntimeURLs(remainingAjaxOptions);

  const jqXHR = $.ajax(adjustedAjaxOptions);
  const bearerTokenRegex = new RegExp(`^${AuthStore.getBearerTokenUrl()}`);

  if (remainingAjaxOptions.url.match(bearerTokenRegex) !== null) {
    jqXHR.then(resolveWithResponse, reject);
  } else {
    jqXHR.then(resolveWithResponse, function(xhr, statusText) {
      if (isXHRAborted(xhr, statusText)) {
        if (rejectOnAbort) {
          reject(xhr, statusText);
        }

        return;
      }

      if (AuthStore.isAuthenticated() && xhr.status === 401) {
        reissueAndTryAgain();
      } else {
        redirectOrReject(xhr);
      }
    });
  }

  function resolveWithResponse(payload, statusText, finalJqXHR) {
    resolve({ payload, statusText, jqXHR: finalJqXHR });
  }

  function redirectOrReject(xhr) {
    const postAuthRoute = AuthStore.getPostAuthRoute();
    const isPostAuthRoute = ajaxOptions.url === postAuthRoute;
    const isForbidden = xhr.status === 403;

    if (
      AuthStore.isAuthenticated() &&
      !isPostAuthRoute &&
      isForbidden &&
      !FreeTrialService.shouldShowExpirationOverlay()
    ) {
      if (rerouteOnUnauthorized !== false) {
        if (toastOnUnauthorized !== false) {
          RouteActions.transitionTo(`${AuthStore.getPostAuthRoute()}?toastError=insufficientPermissions`);
        } else {
          RouteActions.transitionTo(postAuthRoute);
        }
      }
    }

    reject(xhr);
  }

  function reissueAndTryAgain() {
    const reissuePromise = isPlatformActive() ? reissueAuthentication() : AuthStore.reissue();
    reissuePromise.then(
      function() {
        $.ajax(adjustedAjaxOptions).then(resolveWithResponse, redirectOrReject);
      },
      function(error) {
        if (error && !isPlatformActive()) {
          RouteActions.refresh();
        }
        reject(error);
      },
    );
  }

  return { then: promise.then.bind(promise), jqXHR };
};

// a "standard" version of RSVP.defer() that would work with any Promise, or
// so i hope
function defer() {
  let _resolve, _reject;

  return {
    resolve(value) {
      _resolve(value);
    },
    reject(reason) {
      _reject(reason);
    },
    promise: new Promise(function(resolve, reject) {
      _resolve = resolve;
      _reject = reject;
    }),
  };
}



// WEBPACK FOOTER //
// ./jsapp/config/initializers/magnificentAjax.js